import { WorkerStage } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack, ThemeProvider } from "@mui/material";
import { PullToRefresh } from "@src/appV2/lib/PullToRefresh/PullToRefresh";
import { PageHeaderWrapper, Pill, Text } from "@src/appV2/redesign/system/ui-components";
import { getTheme } from "@src/appV2/redesign/system/ui-theme";
import { useHistory } from "react-router-dom";

import { AccountOnHoldCallout } from "../../Charting/AccountOnHoldCallout";
import { CLIPBOARD_SCORE_FULL_PATH } from "../../ClipboardScore/utils/paths";
import { Button } from "../../components/Button";
import { PageWrapper } from "../../components/PageWrapper";
import { SelectableBox } from "../../components/SelectableBox";
import { useIsJobsEnabled } from "../../PlacementCandidate/useIsJobsEnabled";
import { logInviteCalloutViewed } from "../../Shift/Invite/logInviteCalloutViewed";
import { PageContent } from "../../WorkerShifts/components/PageContent";
import { SHIFT_INVITES_FULL_PATH } from "../../WorkerShifts/path";
import { ListViewBanners } from "../ListView/Banners";
import { WorkerReferralCta } from "../ListView/WorkerReferralCta";
import { WorkplaceReferralCta } from "../ListView/WorkplaceReferralCta";
import { BrowseJobsCta } from "./BrowseJobsCta";

interface RestrictedPageContentProps {
  shiftInviteCount: number;
  visitInviteCount: number;
  areInvitesLoaded: boolean;
  workerStage: WorkerStage;
  liftDate?: string;
  onRefresh: () => Promise<void>;
}

export function RestrictedPageContent(props: RestrictedPageContentProps) {
  const { shiftInviteCount, visitInviteCount, areInvitesLoaded, workerStage, liftDate, onRefresh } =
    props;
  const history = useHistory();

  const handleLearnMoreClick = () => {
    history.push(CLIPBOARD_SCORE_FULL_PATH);
  };

  const totalInviteCount = shiftInviteCount + visitInviteCount;
  const isJobsEnabled = useIsJobsEnabled();

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper variant="tertiary">
        <PullToRefresh onRefresh={onRefresh} />

        <AccountOnHoldCallout />

        <PageContent sx={(theme) => ({ paddingTop: theme.spacing(4) })}>
          <PageHeaderWrapper variant="tertiary" sx={{ mx: -5 }}>
            <ListViewBanners showClipboardScoreBanner={false} />
          </PageHeaderWrapper>
          <Stack spacing={5} alignItems="center" mt={8}>
            <Text variant="h3" textAlign="center">
              Your account is Restricted
            </Text>

            <Text
              variant="body2"
              textAlign="center"
              sx={(theme) => ({
                color: theme.palette.text.secondary,
              })}
            >
              Your Clipboard Score reached zero as a result of cancellations or late arrivals.
              You&apos;re temporarily unable to book open shifts.{" "}
              {isJobsEnabled && "This does not impact your access to Jobs on Clipboard"}
            </Text>

            <Stack direction="row" spacing={3}>
              <Stack>
                {isDefined(liftDate) && (
                  <Pill
                    variant="outlined-dark"
                    iconType="clock-filled"
                    label={`Lifts on ${liftDate}`}
                    size="small"
                  />
                )}
                <Button
                  variant="text"
                  size="small"
                  sx={{ textDecoration: "underline" }}
                  onClick={handleLearnMoreClick}
                >
                  Learn More
                </Button>
              </Stack>{" "}
            </Stack>

            <Stack spacing={3} sx={{ pt: 8, width: "100%" }}>
              {isJobsEnabled && <BrowseJobsCta />}
              <SelectableBox
                withCallToActionIcon
                startIconType="email"
                href={SHIFT_INVITES_FULL_PATH}
                onClickCapture={() => {
                  logInviteCalloutViewed({
                    source: "restricted",
                    workerStage,
                    shiftInviteCount,
                    visitInviteCount,
                  });
                }}
              >
                {areInvitesLoaded ? `View invites (${totalInviteCount})` : "View invites"}
              </SelectableBox>
              <WorkerReferralCta />
              <WorkplaceReferralCta />
            </Stack>
          </Stack>
        </PageContent>
      </PageWrapper>
    </ThemeProvider>
  );
}
